// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — PropertiesPanel (M3: V·IANet Flow)
// Right sidebar — M3: Card detalle, M5: Escala ProcessRef,
// M6: Paleta colores, M7: Selector documentos, M9: Panel ancho
// ═══════════════════════════════════════════════════════════════

import { useCallback, useState, useEffect, useRef } from 'react';
import { MarkerType } from '@xyflow/react';
import type {
  FlowNode,
  FlowEdge,
  FlowNodeData,
  TipoNodoFlujograma,
} from '../types/flowchart.types';
import { useI18n } from '../../../../../i18n';
import { request } from '../../../../shared/services/api';
import { FLOW_TOKENS as T } from '../shared/flowTokens';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';

// ─── Edge style/arrow types ─────────────────────────────────────────────────

export type EdgeLineStyle = 'solid' | 'dashed' | 'dotted';
export type EdgeArrowType = 'arrow' | 'none' | 'bidirectional' | 'open';

export interface EdgeProperties {
  label?: string;
  lineStyle?: EdgeLineStyle;
  arrowType?: EdgeArrowType;
  color?: string;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface PropertiesPanelProps {
  selectedNode: FlowNode | null;
  selectedEdge?: FlowEdge | null;
  onUpdateNode: (nodeId: string, data: Partial<FlowNodeData>) => void;
  onUpdateEdge?: (edgeId: string, props: EdgeProperties) => void;
  onClose?: () => void;
  nodePosition?: { x: number; y: number } | null;
  onResizeNode?: (nodeId: string, scale: number) => void;
  fases?: { id: string; nombre: string }[];
  procesoId?: string;
}

// ─── Data fetching hook ─────────────────────────────────────────────────────

interface SelectOption {
  value: string;
  label: string;
}

function useSelectorData(procesoId?: string) {
  const [puestos, setPuestos] = useState<SelectOption[]>([]);
  const [documentos, setDocumentos] = useState<SelectOption[]>([]);
  const [procesos, setProcesos] = useState<SelectOption[]>([]);
  const [loadingPuestos, setLoadingPuestos] = useState(false);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [loadingProcesos, setLoadingProcesos] = useState(false);

  useEffect(() => {
    type ListResponse = { data?: unknown[]; datos?: unknown[] } | unknown[];
    setLoadingPuestos(true);
    request<ListResponse>('/puestos?porPagina=100')
      .then((body) => {
        const list = (
          Array.isArray(body)
            ? body
            : ((body as { datos?: unknown[]; data?: unknown[] }).datos ??
              (body as { data?: unknown[] }).data ??
              [])
        ) as Array<{
          id: string;
          titulo?: string;
          nombre?: string;
          codigo?: string;
        }>;
        setPuestos(
          list.map((p) => ({ value: p.id, label: p.titulo ?? p.nombre ?? p.codigo ?? p.id })),
        );
      })
      .catch(() => setPuestos([]))
      .finally(() => setLoadingPuestos(false));

    setLoadingProcesos(true);
    request<ListResponse>('/procesos?porPagina=100')
      .then((body) => {
        const list = (
          Array.isArray(body)
            ? body
            : ((body as { datos?: unknown[]; data?: unknown[] }).datos ??
              (body as { data?: unknown[] }).data ??
              [])
        ) as Array<{
          id: string;
          codigo?: string;
          nombre?: string;
        }>;
        setProcesos(
          list.map((p) => ({
            value: p.id,
            label: `${p.codigo ?? ''} ${p.nombre ?? ''}`.trim() || p.id,
          })),
        );
      })
      .catch(() => setProcesos([]))
      .finally(() => setLoadingProcesos(false));
  }, []);

  useEffect(() => {
    if (!procesoId) return;
    type ListResponse = { data?: unknown[]; datos?: unknown[] } | unknown[];
    setLoadingDocumentos(true);
    request<ListResponse>(`/documentos?procesoId=${procesoId}&porPagina=100`)
      .then((body) => {
        const list = (
          Array.isArray(body)
            ? body
            : ((body as { datos?: unknown[]; data?: unknown[] }).datos ??
              (body as { data?: unknown[] }).data ??
              [])
        ) as Array<{
          id: string;
          codigo?: string;
          titulo?: string;
          nombre?: string;
        }>;
        setDocumentos(
          list.map((d) => ({
            value: d.id,
            label: `${d.codigo ?? ''} ${d.titulo ?? d.nombre ?? ''}`.trim() || d.id,
          })),
        );
      })
      .catch(() => setDocumentos([]))
      .finally(() => setLoadingDocumentos(false));
  }, [procesoId]);

  return { puestos, documentos, procesos, loadingPuestos, loadingDocumentos, loadingProcesos };
}

// ─── M9: Ancho del panel y tamaños de fuente más grandes ─────────────────────

const PANEL_WIDTH = 320;

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  fontFamily: T.font.family,
  color: T.color.textSecondary,
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '13px',
  fontFamily: T.font.family,
  padding: '6px 8px',
  borderRadius: 4,
  border: `1px solid ${T.color.border}`,
  outline: 'none',
  color: T.color.textPrimary,
  backgroundColor: T.color.surface,
  boxSizing: 'border-box' as const,
};

// ─── M6: Paleta de colores corporativos para conectores ──────────────────────

const CONNECTOR_COLORS = [
  '#1F3864',
  '#2F5496',
  '#33BCE1',
  '#595959',
  '#000000',
  '#375623',
  '#7B0000',
  '#D9D9D9',
  '#E06C00',
  '#6B21A8',
];

// ─── Field Components ───────────────────────────────────────────────────────

function TextInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={labelStyle}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
      />
    </div>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder,
  rows = 2,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={labelStyle}>{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={labelStyle}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── M6: Color Palette Component ─────────────────────────────────────────────

function ColorPalette({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {CONNECTOR_COLORS.map((color) => (
          <button
            key={color}
            onClick={() => onChange(color)}
            title={color}
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              border:
                value === color ? `2px solid ${T.color.secondary}` : `1px solid ${T.color.border}`,
              backgroundColor: color,
              cursor: 'pointer',
              outline: 'none',
              boxShadow: value === color ? `0 0 0 2px ${T.color.secondary}40` : 'none',
              transition: 'box-shadow 0.12s',
            }}
          />
        ))}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#374151"
        style={{ ...inputStyle, fontSize: '11px' }}
      />
    </div>
  );
}

// ─── M7: Selector de documentos con busqueda ─────────────────────────────────

function DocumentSelector({
  label,
  documentos,
  selectedIds,
  onChange,
  loading,
  texts,
}: {
  label: string;
  documentos: SelectOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  loading: boolean;
  texts?: {
    loading?: string;
    search?: string;
    remove?: string;
    noResults?: (s: string) => string;
    empty?: string;
  };
}) {
  const [search, setSearch] = useState('');
  const filtered = documentos.filter((d) => d.label.toLowerCase().includes(search.toLowerCase()));

  if (loading) {
    return (
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>{label}</label>
        <LoadingSpinner text={texts?.loading ?? 'Loading...'} size="sm" />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <label style={labelStyle}>{label}</label>
      {/* Documentos seleccionados */}
      {selectedIds.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
          {selectedIds.map((id) => {
            const doc = documentos.find((d) => d.value === id);
            return (
              <div
                key={id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 8px',
                  backgroundColor: T.color.surfaceAlt,
                  borderRadius: 4,
                  fontSize: '11px',
                  fontFamily: T.font.family,
                  color: T.color.textPrimary,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {doc?.label ?? id}
                </span>
                <button
                  onClick={() => onChange(selectedIds.filter((i) => i !== id))}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: T.color.textTertiary,
                    padding: '0 2px',
                    lineHeight: 1,
                  }}
                  title={texts?.remove ?? 'Remove'}
                >
                  &times;
                </button>
              </div>
            );
          })}
        </div>
      )}
      {/* Busqueda */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={texts?.search ?? 'Search...'}
        style={{ ...inputStyle, fontSize: '12px', marginBottom: 4 }}
      />
      {search && filtered.length > 0 && (
        <div
          style={{
            maxHeight: 120,
            overflowY: 'auto',
            border: `1px solid ${T.color.border}`,
            borderRadius: 4,
            backgroundColor: T.color.surface,
          }}
        >
          {filtered.slice(0, 10).map((doc) => (
            <button
              key={doc.value}
              onClick={() => {
                if (!selectedIds.includes(doc.value)) {
                  onChange([...selectedIds, doc.value]);
                }
                setSearch('');
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '5px 8px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                fontSize: '11px',
                fontFamily: T.font.family,
                color: T.color.textPrimary,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.backgroundColor = T.color.surfaceSubtle;
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              {doc.label}
            </button>
          ))}
        </div>
      )}
      {search && filtered.length === 0 && (
        <div
          style={{
            fontSize: '11px',
            fontFamily: T.font.family,
            color: T.color.textTertiary,
            padding: '4px 0',
          }}
        >
          {texts?.noResults ? texts.noResults(search) : `No results for "${search}"`}
        </div>
      )}
      {!search && documentos.length === 0 && (
        <div
          style={{
            padding: '6px 8px',
            fontSize: '11px',
            fontFamily: T.font.family,
            color: T.color.textTertiary,
            border: `1px dashed ${T.color.border}`,
            borderRadius: 4,
          }}
        >
          {texts?.empty ?? 'No documents available.'}
        </div>
      )}
    </div>
  );
}

// ─── Node Type Labels ────────────────────────────────────────────────────────

// Node type labels resolved in component with i18n

// ─── M5: Opciones de escala para ProcessRef ──────────────────────────────────

const SIZE_SCALE_OPTIONS = [
  { value: '0.75', label: '75%' },
  { value: '1', label: '100%' },
  { value: '1.25', label: '125%' },
  { value: '1.5', label: '150%' },
];

// ─── Component ───────────────────────────────────────────────────────────────

// ─── Floating card container style ──────────────────────────────────────────

function getFloatingStyle(x: number, y: number): React.CSSProperties {
  return {
    position: 'absolute',
    left: x,
    top: y,
    width: PANEL_WIDTH,
    maxHeight: 'calc(100% - 32px)',
    backgroundColor: T.color.surface,
    borderRadius: 8,
    borderTop: `2.5px solid ${T.color.navy}`,
    boxShadow: '0 4px 16px rgba(31,56,100,0.15)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 30,
    overflow: 'hidden',
  };
}

function useDragPanel(initialPos: { x: number; y: number } | null) {
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragging = useRef(false);
  const start = useRef({ mx: 0, my: 0, ox: 0, oy: 0 });

  // Reset offset when panel position changes (new node selected)
  useEffect(() => {
    setOffset({ x: 0, y: 0 });
  }, [initialPos?.x, initialPos?.y]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragging.current = true;
      start.current = { mx: e.clientX, my: e.clientY, ox: offset.x, oy: offset.y };
      const onMove = (ev: MouseEvent) => {
        if (!dragging.current) return;
        setOffset({
          x: start.current.ox + ev.clientX - start.current.mx,
          y: start.current.oy + ev.clientY - start.current.my,
        });
      };
      const onUp = () => {
        dragging.current = false;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [offset],
  );

  const pos = initialPos
    ? { x: initialPos.x + offset.x, y: initialPos.y + offset.y }
    : { x: 16 + offset.x, y: 16 + offset.y };
  return { pos, onMouseDown };
}

export default function PropertiesPanel({
  selectedNode,
  selectedEdge,
  onUpdateNode,
  onUpdateEdge,
  onClose: _onClose,
  nodePosition,
  onResizeNode,
  fases = [],
  procesoId,
}: PropertiesPanelProps) {
  const { t } = useI18n();
  const nodeTypeLabels: Record<TipoNodoFlujograma, string> = {
    ACTIVIDAD: t.flow.tipoActividad,
    DECISION: t.flow.tipoDecision,
    DOCUMENTO: t.flow.tipoDocumento,
    SUBPROCESO: t.flow.tipoSubproceso,
    INICIO: t.flow.tipoInicio,
    FIN: t.flow.tipoFin,
    PROCESO_REF: t.flow.tipoProcesoRef,
    PROCESO_GOBIERNO: t.flow.tipoProcesoGobierno,
    RESULTADO: t.flow.tipoResultado,
  };
  const data = selectedNode?.data as FlowNodeData | undefined;
  const nodeType = data?.nodeType;
  const { puestos, documentos, procesos, loadingPuestos, loadingDocumentos, loadingProcesos } =
    useSelectorData(procesoId);
  const { pos: dragPos, onMouseDown: onDragStart } = useDragPanel(nodePosition ?? null);

  const update = useCallback(
    (field: string, value: unknown) => {
      if (!selectedNode) return;
      onUpdateNode(selectedNode.id, { [field]: value } as Partial<FlowNodeData>);
    },
    [selectedNode, onUpdateNode],
  );

  // ─── Edge Properties ───
  if (selectedEdge && !selectedNode) {
    const edgeStyle = selectedEdge.style as Record<string, unknown> | undefined;
    const currentDasharray = edgeStyle?.strokeDasharray as string | undefined;
    const currentLineStyle: EdgeLineStyle =
      currentDasharray === '6 3' ? 'dashed' : currentDasharray === '2 2' ? 'dotted' : 'solid';
    const currentMarkerEnd = selectedEdge.markerEnd;
    const currentMarkerStart = selectedEdge.markerStart;
    const currentArrowType: EdgeArrowType = !currentMarkerEnd
      ? 'none'
      : currentMarkerStart
        ? 'bidirectional'
        : typeof currentMarkerEnd === 'object' &&
            (currentMarkerEnd as any).type === MarkerType.Arrow
          ? 'open'
          : 'arrow';
    const currentColor = (edgeStyle?.stroke as string) ?? '#374151';

    return (
      <div style={getFloatingStyle(dragPos.x, dragPos.y)}>
        <div
          onMouseDown={onDragStart}
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${T.color.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'grab',
          }}
        >
          <h3
            style={{
              fontSize: '11px',
              fontWeight: 700,
              fontFamily: T.font.family,
              color: T.color.navy,
              textTransform: 'uppercase',
              letterSpacing: '0.7px',
              margin: 0,
            }}
          >
            {t.flow.conector}
          </h3>
        </div>
        <div style={{ padding: '12px 16px' }}>
          <TextInput
            label={t.flow.etiqueta}
            value={(selectedEdge.label as string) ?? ''}
            onChange={(v) => onUpdateEdge?.(selectedEdge.id, { label: v || undefined })}
            placeholder={t.flow.etiquetaConector}
          />
          <SelectField
            label={t.flow.estiloLinea}
            value={currentLineStyle}
            onChange={(v) => onUpdateEdge?.(selectedEdge.id, { lineStyle: v as EdgeLineStyle })}
            options={[
              { value: 'solid', label: t.flow.solida },
              { value: 'dashed', label: t.flow.discontinua },
              { value: 'dotted', label: t.flow.punteada },
            ]}
          />
          <SelectField
            label={t.flow.tipoFlecha}
            value={currentArrowType}
            onChange={(v) => onUpdateEdge?.(selectedEdge.id, { arrowType: v as EdgeArrowType })}
            options={[
              { value: 'arrow', label: t.flow.flecha },
              { value: 'none', label: t.flow.sinFlecha },
              { value: 'bidirectional', label: t.flow.bidireccional },
              { value: 'open', label: t.flow.abierta },
            ]}
          />
          {/* M6: Paleta de colores */}
          <ColorPalette
            label={t.flow.colorLabel}
            value={currentColor}
            onChange={(v) => onUpdateEdge?.(selectedEdge.id, { color: v })}
          />
        </div>
        <div
          style={{
            padding: '8px 16px',
            backgroundColor: T.color.surfaceSubtle,
            borderTop: `1px solid ${T.color.border}`,
          }}
        >
          <div style={{ fontSize: '10px', fontFamily: 'monospace', color: T.color.textTertiary }}>
            ID: {selectedEdge.id}
          </div>
        </div>
      </div>
    );
  }

  // ─── Empty State — panel hidden when nothing selected ───
  if (!selectedNode || !data) {
    return null;
  }

  // ─── Buscar nombre del puesto responsable (para M3 card) ───
  const puestoNombre = (() => {
    const puestoId = (data as any).puestoId;
    if (!puestoId) return null;
    const puesto = puestos.find((p) => p.value === puestoId);
    return puesto?.label ?? null;
  })();

  // ─── Buscar nombre de fase ───
  const faseNombre = (() => {
    if (!data.fase) return null;
    const fase = fases.find((f) => f.id === data.fase);
    return fase?.nombre ?? data.fase;
  })();

  // ─── With Selection ───
  return (
    <div style={getFloatingStyle(dragPos.x, dragPos.y)}>
      {/* Header: PROPIEDADES + size controls — draggable */}
      <div
        onMouseDown={onDragStart}
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${T.color.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          cursor: 'grab',
        }}
      >
        <h3
          style={{
            fontSize: '11px',
            fontWeight: 700,
            fontFamily: T.font.family,
            color: T.color.navy,
            textTransform: 'uppercase',
            letterSpacing: '0.7px',
            margin: 0,
          }}
        >
          {t.flow.propiedades}
        </h3>
        {selectedNode && onResizeNode && (
          <div style={{ display: 'flex', gap: 3 }}>
            {[0.25, 0.5, 0.75, 1].map((scale) => (
              <button
                key={scale}
                onClick={() => onResizeNode(selectedNode.id, scale)}
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  fontFamily: T.font.family,
                  padding: '2px 5px',
                  borderRadius: 3,
                  border: `1px solid ${T.color.border}`,
                  background: T.color.surface,
                  color: T.color.textSecondary,
                  cursor: 'pointer',
                }}
                title={`Tama\u00f1o ${scale * 100}%`}
              >
                {scale * 100}%
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {/* M3: Card de detalle del nodo */}
        <div
          style={{
            padding: '16px',
            borderBottom: `1px solid ${T.color.border}`,
            backgroundColor: T.color.surface,
          }}
        >
          {/* Nombre del nodo */}
          <div
            style={{
              fontSize: '14px',
              fontWeight: 700,
              fontFamily: T.font.family,
              color: T.color.navy,
              marginBottom: 8,
              lineHeight: 1.3,
            }}
          >
            {data.label || t.flow.sinNombre}
          </div>

          {/* Badge de tipo */}
          {nodeType && (
            <span
              style={{
                display: 'inline-block',
                fontSize: '10px',
                fontWeight: 600,
                fontFamily: T.font.family,
                color: T.color.surface,
                backgroundColor: T.color.navy,
                padding: '2px 8px',
                borderRadius: 10,
                marginBottom: 8,
                letterSpacing: '0.3px',
              }}
            >
              {nodeTypeLabels[nodeType]}
            </span>
          )}

          {/* Responsable */}
          <div
            style={{
              fontSize: '11px',
              fontFamily: T.font.family,
              color: T.color.textSecondary,
              marginBottom: 4,
            }}
          >
            <strong style={{ fontWeight: 600, color: T.color.textPrimary }}>
              {t.flow.responsable}:
            </strong>{' '}
            {puestoNombre || t.flow.sinAsignar}
          </div>

          {/* Descripcion */}
          {data.description && (
            <div
              style={{
                fontSize: '11px',
                fontFamily: T.font.family,
                color: T.color.textSecondary,
                marginBottom: 4,
                lineHeight: 1.4,
              }}
            >
              <strong style={{ fontWeight: 600, color: T.color.textPrimary }}>
                {t.flow.descripcionLabel}:
              </strong>{' '}
              {data.description}
            </div>
          )}

          {/* Fase */}
          {faseNombre && (
            <div
              style={{ fontSize: '11px', fontFamily: T.font.family, color: T.color.textSecondary }}
            >
              <strong style={{ fontWeight: 600, color: T.color.textPrimary }}>
                {t.flow.faseLabel}:
              </strong>{' '}
              {faseNombre}
            </div>
          )}
        </div>

        {/* Fields */}
        <div style={{ padding: '12px 16px' }}>
          {/* Campos comunes: nombre + descripcion */}
          <TextInput
            label={t.flow.nombre}
            value={data.label ?? ''}
            onChange={(v) => update('label', v)}
            placeholder={t.common.nodeName}
          />
          <TextArea
            label={t.flow.descripcionLabel}
            value={data.description ?? ''}
            onChange={(v) => update('description', v)}
            placeholder={t.common.nodeDescription}
          />

          {/* Responsable (para ACTIVIDAD) */}
          {nodeType === 'ACTIVIDAD' &&
            (loadingPuestos ? (
              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>{t.flow.responsable}</label>
                <LoadingSpinner text={t.flow.cargandoPuestos} size="sm" />
              </div>
            ) : puestos.length > 0 ? (
              <SelectField
                label={t.flow.responsable}
                value={(data as any).puestoId ?? ''}
                onChange={(v) => update('puestoId', v)}
                options={puestos}
                placeholder={t.common.selectPosition}
              />
            ) : (
              <TextInput
                label={t.flow.responsableId}
                value={(data as any).puestoId ?? ''}
                onChange={(v) => update('puestoId', v)}
                placeholder={t.flow.idPuesto}
              />
            ))}

          {/* Fase — todos excepto INICIO/FIN */}
          {nodeType !== 'INICIO' && nodeType !== 'FIN' && (
            <SelectField
              label={t.flow.faseLabel}
              value={data.fase ?? ''}
              onChange={(v) => update('fase', v)}
              options={
                fases.length > 0
                  ? fases.map((f) => ({ value: f.id, label: f.nombre }))
                  : [
                      { value: 'DIAGNOSTICO', label: t.flow.faseDiagnostico },
                      { value: 'ARQUITECTURA', label: t.flow.faseArquitectura },
                      { value: 'DESPLIEGUE', label: t.flow.faseDespliegue },
                      { value: 'OPERACION', label: t.flow.faseOperacion },
                      { value: 'MADUREZ', label: t.flow.faseMadurez },
                    ]
              }
              placeholder={t.common.noPhase}
            />
          )}

          {/* ACTIVIDAD: M7 — Selector de documentos con busqueda */}
          {nodeType === 'ACTIVIDAD' && (
            <DocumentSelector
              label={t.flow.documentosVinculados}
              documentos={documentos}
              selectedIds={(data as any).documentoId ? [(data as any).documentoId] : []}
              onChange={(ids) => update('documentoId', ids[0] ?? '')}
              loading={loadingDocumentos}
              texts={{
                loading: t.flow.cargandoDocumentos,
                search: t.flow.buscarDocumento,
                remove: t.flow.eliminarDoc,
                noResults: t.flow.sinResultadosPara,
                empty: t.flow.sinDocumentosDisponibles,
              }}
            />
          )}

          {/* DECISION: condicion + etiquetas */}
          {nodeType === 'DECISION' && (
            <>
              <TextInput
                label={t.flow.condicion}
                value={(data as any).condicion ?? ''}
                onChange={(v) => update('condicion', v)}
                placeholder={t.common.conditionExample}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <TextInput
                  label={t.flow.si}
                  value={(data as any).etiquetaSi ?? t.flow.si}
                  onChange={(v) => update('etiquetaSi', v)}
                />
                <TextInput
                  label={t.flow.no}
                  value={(data as any).etiquetaNo ?? t.flow.no}
                  onChange={(v) => update('etiquetaNo', v)}
                />
              </div>
            </>
          )}

          {/* DOCUMENTO: M7 — Selector de documentos con busqueda */}
          {nodeType === 'DOCUMENTO' && (
            <DocumentSelector
              label={t.flow.documentoReferencia}
              documentos={documentos}
              selectedIds={(data as any).documentoId ? [(data as any).documentoId] : []}
              onChange={(ids) => update('documentoId', ids[0] ?? '')}
              loading={loadingDocumentos}
              texts={{
                loading: t.flow.cargandoDocumentos,
                search: t.flow.buscarDocumento,
                remove: t.flow.eliminarDoc,
                noResults: t.flow.sinResultadosPara,
                empty: t.flow.sinDocumentosDisponibles,
              }}
            />
          )}

          {/* PROCESO_REF / PROCESO_GOBIERNO */}
          {(nodeType === 'PROCESO_REF' || nodeType === 'PROCESO_GOBIERNO') &&
            (loadingProcesos ? (
              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>{t.flow.procesoReferencia}</label>
                <LoadingSpinner text={t.flow.cargandoProcesos} size="sm" />
              </div>
            ) : procesos.length > 0 ? (
              <SelectField
                label={t.flow.procesoReferencia}
                value={(data as any).procesoId ?? ''}
                onChange={(v) => update('procesoId', v)}
                options={procesos}
                placeholder={t.common.selectProcess}
              />
            ) : (
              <TextInput
                label={t.flow.procesoIdLabel}
                value={(data as any).procesoId ?? ''}
                onChange={(v) => update('procesoId', v)}
                placeholder={t.flow.idProceso}
              />
            ))}

          {/* M5: Control de tamano para PROCESO_REF */}
          {nodeType === 'PROCESO_REF' && (
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{t.flow.tamano}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {SIZE_SCALE_OPTIONS.map((opt) => {
                  const isActive = String((data as any).sizeScale ?? 1) === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => update('sizeScale', parseFloat(opt.value))}
                      style={{
                        flex: 1,
                        padding: '4px 0',
                        borderRadius: 4,
                        border: `1px solid ${isActive ? T.color.secondary : T.color.border}`,
                        backgroundColor: isActive ? '#F0FBFF' : 'transparent',
                        color: isActive ? T.color.secondary : T.color.textSecondary,
                        fontSize: '11px',
                        fontWeight: 600,
                        fontFamily: T.font.family,
                        cursor: 'pointer',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* INICIO / FIN */}
          {(nodeType === 'INICIO' || nodeType === 'FIN') && (
            <div
              style={{
                fontSize: '11px',
                fontFamily: T.font.family,
                color: T.color.textTertiary,
                marginTop: 4,
              }}
            >
              {t.flow.nodoTipoInfo(nodeTypeLabels[nodeType].toLowerCase())}
            </div>
          )}

          {/* SUBPROCESO */}
          {nodeType === 'SUBPROCESO' && (
            <div
              style={{
                fontSize: '11px',
                fontFamily: T.font.family,
                color: T.color.textTertiary,
                marginTop: 4,
              }}
            >
              {t.flow.subprocesoInfo}
            </div>
          )}
        </div>

        {/* Info section */}
        <div
          style={{
            padding: '8px 16px',
            backgroundColor: T.color.surfaceSubtle,
            borderTop: `1px solid ${T.color.border}`,
          }}
        >
          <div style={{ fontSize: '10px', fontFamily: 'monospace', color: T.color.textTertiary }}>
            ID: {selectedNode.id}
          </div>
        </div>
      </div>
      {/* end scrollable body */}
    </div>
  );
}
